import React, { useState } from 'react';
import { Rocket, Copy, Check, ExternalLink, ChevronDown, ChevronRight, FileCode, FileText, Terminal } from 'lucide-react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import api from '../../utils/api';

const MODEL_MAP = {
  'auto': { name: 'Auto', icon: 'A', color: 'bg-cyan-500/20 text-cyan-400' },
  'opus-4.7': { name: 'Claude Opus 4.7', icon: 'O7', color: 'bg-orange-500/20 text-orange-400' },
  'sonnet-4.7': { name: 'Claude Sonnet 4.7', icon: 'S7', color: 'bg-violet-500/20 text-violet-400' },
  'opus-4.6': { name: 'Claude Opus 4.6', icon: 'O6', color: 'bg-orange-500/20 text-orange-300' },
  'sonnet-4.6': { name: 'Claude Sonnet 4.6', icon: 'S6', color: 'bg-violet-500/20 text-violet-300' },
  'claude': { name: 'Claude Sonnet 4', icon: 'C', color: 'bg-purple-500/20 text-purple-400' },
  'openai': { name: 'GPT-5.2', icon: 'G', color: 'bg-emerald-500/20 text-emerald-400' },
  'o3': { name: 'OpenAI o3', icon: 'o3', color: 'bg-emerald-500/20 text-emerald-300' },
  'grok': { name: 'Grok (GPT-4.1)', icon: 'X', color: 'bg-amber-500/20 text-amber-400' },
  'gemini': { name: 'Gemini 3.1 Pro', icon: 'Gm', color: 'bg-sky-500/20 text-sky-400' },
  'gemini-flash': { name: 'Gemini 3 Flash', icon: 'Gf', color: 'bg-sky-500/20 text-sky-300' },
};

const AGENT_MAP = {
  'e1': { name: 'E1', icon: 'E1', color: 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white' },
  'frontend': { name: 'Frontend', icon: 'FE', color: 'bg-blue-500/20 text-blue-400' },
  'backend': { name: 'Backend', icon: 'BE', color: 'bg-emerald-500/20 text-emerald-400' },
  'testing': { name: 'Testing', icon: 'QA', color: 'bg-amber-500/20 text-amber-400' },
  'deploy': { name: 'Deploy', icon: 'CD', color: 'bg-purple-500/20 text-purple-400' },
  'coordinator': { name: 'E1', icon: 'E1', color: 'bg-gradient-to-br from-blue-600 to-cyan-500 text-white' }
};

export const getModelDisplay = (modelName) => MODEL_MAP[modelName] || MODEL_MAP['auto'];
export const getAgentDisplay = (agentType) => AGENT_MAP[agentType] || AGENT_MAP['coordinator'];

const LANG_MAP = {
  'js': 'javascript', 'ts': 'typescript', 'py': 'python',
  'jsx': 'jsx', 'tsx': 'tsx', 'html': 'html', 'css': 'css',
  'json': 'json', 'bash': 'bash', 'sh': 'bash', 'sql': 'sql',
  'yaml': 'yaml', 'yml': 'yaml', 'dockerfile': 'docker',
  'python': 'python', 'javascript': 'javascript', 'typescript': 'typescript'
};

const FILE_ICONS = {
  jsx: FileCode, tsx: FileCode, js: FileCode, ts: FileCode,
  py: FileCode, html: FileText, css: FileText, json: FileText,
  bash: Terminal, sh: Terminal, sql: FileText, yaml: FileText,
};

// Detect file path from first line of code or language hint
function detectFilePath(language, content) {
  const firstLine = content.split('\n')[0];
  // Match patterns like: // src/App.jsx, # routes/auth.py, <!-- index.html -->
  const pathMatch = firstLine.match(/^(?:\/\/|#|<!--)\s*([\w./\-]+\.\w+)/);
  if (pathMatch) return pathMatch[1];
  return null;
}

function parseContent(text) {
  const parts = [];
  const codeRegex = /```(\w+)?\n([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;
  while ((match = codeRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }
    const lang = match[1] || 'text';
    const code = match[2].trim();
    const filePath = detectFilePath(lang, code);
    // Strip file path comment from code display
    const cleanCode = filePath ? code.split('\n').slice(1).join('\n').trim() : code;
    parts.push({ type: 'code', language: lang, content: cleanCode || code, filePath });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ type: 'text', content: text.slice(lastIndex) });
  }
  return parts.length ? parts : [{ type: 'text', content: text }];
}

function isDeployableCode(language, content) {
  const lang = (language || '').toLowerCase();
  if (['html', 'htm'].includes(lang) && content.includes('<')) return true;
  if (['jsx', 'tsx', 'javascript', 'js'].includes(lang) && content.includes('export')) return true;
  return false;
}

const CodeBlock = ({ language, content, filePath, projectId, projectName }) => {
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [deployUrl, setDeployUrl] = useState(null);
  const lang = LANG_MAP[language?.toLowerCase()] || language || 'text';
  const deployable = isDeployableCode(language, content);
  const lineCount = content.split('\n').length;
  const IconComponent = FILE_ICONS[language?.toLowerCase()] || FileCode;

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDeploy = async () => {
    if (!projectId) return;
    setDeploying(true);
    try {
      const isHtml = content.includes('<html') || content.includes('<!DOCTYPE');
      const fileName = isHtml ? 'index.html' : 'index.js';
      const fileData = btoa(unescape(encodeURIComponent(content)));
      const res = await api.post('/deploy/vercel/quick', {
        project_id: projectId, project_name: projectName || 'chat-deploy',
        files: [{ file: fileName, data: fileData }]
      });
      if (res.data.success) setDeployUrl(res.data.url);
    } catch (err) { console.error('Deploy failed:', err); }
    finally { setDeploying(false); }
  };

  return (
    <div className="my-3 rounded-lg overflow-hidden border border-border" data-testid="code-block">
      {/* File header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-900 border-b border-slate-800">
        <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-xs hover:text-foreground transition-colors min-w-0">
          {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
          <IconComponent className="w-3.5 h-3.5 text-blue-400 shrink-0" />
          {filePath ? (
            <span className="text-blue-400 font-mono truncate">{filePath}</span>
          ) : (
            <span className="text-muted-foreground font-mono">{lang}</span>
          )}
          <span className="text-muted-foreground/70 text-[11px] shrink-0">{lineCount} lines</span>
        </button>
        <div className="flex items-center gap-1">
          {deployable && !deployUrl && (
            <button onClick={handleDeploy} disabled={deploying}
              className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-violet-500/15 text-violet-400 hover:bg-violet-500/25 border border-violet-500/20 transition-all disabled:opacity-50"
              data-testid="deploy-code-btn">
              <Rocket className="w-3 h-3" /> {deploying ? 'Deploying...' : 'Deploy'}
            </button>
          )}
          {deployUrl && (
            <a href={deployUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md bg-emerald-500/15 text-emerald-400 border border-emerald-500/20"
              data-testid="deploy-live-link">
              <ExternalLink className="w-3 h-3" /> Live
            </a>
          )}
          <button onClick={handleCopy}
            className="flex items-center gap-1 px-2 py-1 text-[11px] rounded-md text-muted-foreground hover:text-foreground/80 hover:bg-slate-800 transition-all"
            data-testid="copy-code-btn">
            {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      {/* Code content */}
      {!collapsed && (
        <SyntaxHighlighter
          language={lang} style={oneDark}
          customStyle={{ margin: 0, padding: '0.875rem 1rem', fontSize: '0.825rem', lineHeight: '1.6', background: '#0d0d0d', maxHeight: '450px', overflow: 'auto' }}
          showLineNumbers={lineCount > 5}
          lineNumberStyle={{ color: '#3a3a3a', paddingRight: '1rem', minWidth: '2.5em', fontSize: '0.7rem' }}
          wrapLongLines>
          {content}
        </SyntaxHighlighter>
      )}
      {collapsed && (
        <div className="px-3 py-2 text-xs text-muted-foreground italic bg-slate-900">Code collapsed ({lineCount} lines) — click header to expand</div>
      )}
    </div>
  );
};

const MarkdownText = ({ text }) => {
  const lines = text.split('\n');
  return (
    <div className="text-sm leading-[1.75]">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('### ')) return <h3 key={i} className="text-base font-semibold text-foreground mt-4 mb-1.5">{formatInline(trimmed.slice(4))}</h3>;
        if (trimmed.startsWith('## ')) return <h2 key={i} className="text-lg font-semibold text-foreground mt-4 mb-1.5">{formatInline(trimmed.slice(3))}</h2>;
        if (trimmed.startsWith('# ')) return <h1 key={i} className="text-xl font-bold text-foreground mt-4 mb-1.5">{formatInline(trimmed.slice(2))}</h1>;
        if (trimmed.startsWith('- [ ] ')) return <li key={i} className="ml-4 flex items-start gap-2 text-foreground/80"><span className="mt-1 w-4 h-4 rounded border border-border shrink-0" />{formatInline(trimmed.slice(6))}</li>;
        if (trimmed.startsWith('- [x] ')) return <li key={i} className="ml-4 flex items-start gap-2 text-foreground/80"><span className="mt-1 w-4 h-4 rounded bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0"><Check className="w-2.5 h-2.5 text-emerald-400" /></span>{formatInline(trimmed.slice(6))}</li>;
        if (trimmed.startsWith('- ')) return <li key={i} className="ml-4 text-foreground/80 before:content-['•'] before:text-blue-400 before:mr-2">{formatInline(trimmed.slice(2))}</li>;
        if (/^\d+\.\s/.test(trimmed)) {
          const num = trimmed.match(/^(\d+)\./)[1];
          return <li key={i} className="ml-4 text-foreground/80 flex items-start gap-2"><span className="text-primary font-semibold text-xs bg-primary/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{num}</span><span>{formatInline(trimmed.replace(/^\d+\.\s/, ''))}</span></li>;
        }
        if (trimmed.startsWith('> ')) return <blockquote key={i} className="ml-0 pl-3 border-l-2 border-primary/30 text-muted-foreground italic">{formatInline(trimmed.slice(2))}</blockquote>;
        if (trimmed.startsWith('---') || trimmed.startsWith('***')) return <hr key={i} className="my-3 border-border" />;
        if (trimmed === '') return <div key={i} className="h-2" />;
        return <p key={i} className="text-foreground/80">{formatInline(line)}</p>;
      })}
    </div>
  );
};

function formatInline(text) {
  return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i} className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono border border-slate-200">{part.slice(1, -1)}</code>;
    // Markdown links
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) return <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 underline underline-offset-2">{linkMatch[1]}</a>;
    return part;
  });
}

const ChatMessage = ({ message, userName, isAuto, projectId, projectName }) => {
  const isUser = message.role === 'user';
  const agent = getAgentDisplay(message.agentType);
  const model = getModelDisplay(message.selectedModel);
  const parts = !isUser ? parseContent(message.content || '') : [];

  if (isUser) {
    return (
      <div data-testid="message-user" className="flex justify-end">
        <div className="max-w-xl bg-secondary border border-input text-foreground rounded-2xl rounded-tr-sm px-4 py-3">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="message-assistant" className="flex gap-3">
      <div className={`w-7 h-7 rounded-lg ${agent.color} flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5`}>{agent.icon}</div>
      <div className="flex-1 min-w-0">
        {/* Agent + model label */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-semibold text-foreground">{agent.name}</span>
          {message.selectedModel && isAuto && (
            <span className="text-xs text-muted-foreground">via {model.name}</span>
          )}
          <span className="text-xs text-muted-foreground/70">{new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
        </div>
        {/* Content — flat layout */}
        <div className="space-y-0">
          {parts.map((part, i) =>
            part.type === 'code' ? (
              <CodeBlock key={i} language={part.language} content={part.content} filePath={part.filePath} projectId={projectId} projectName={projectName} />
            ) : (
              <MarkdownText key={i} text={part.content} />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
